import React from "react";
import clsx from "clsx";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import PlaybookBuildService from "../../services/PlaybookBuildService";
import AgentSourceIcon from "./AgentSourceIcon";

import "./build-page.css";
import "./playbook-build-page.css";

class PlaybookBuildSourcesCard extends WComponent {
	constructor(props) {
		super(props);
		this.fileInputRef = React.createRef();
	}

	sourceNamed = ({ key }) => {
		const source = this.props.session.sourceCatalogue.find(candidate => candidate.key === key);
		return source;
	};

	onChooseFiles = () => {
		if (this.fileInputRef.current) this.fileInputRef.current.click();
	};

	onFilesChosen = event => {
		const files = Array.from(event.target.files || []);
		event.target.value = "";
		if (files.length === 0) return;
		this.props.onUploadFiles({ files });
	};

	renderChosenSource = chosen => {
		const { session, isSavingSources, onSetSourceNeed, onRemoveSource } = this.props;
		const source = this.sourceNamed({ key: chosen.key });
		const isOpen = session.status === "open";

		return (
			<div key={chosen.key} className="playbook-build__source">
				<AgentSourceIcon icon={source.icon} />
				<div className="playbook-build__source-text">
					<span className="playbook-build__source-name">
						{source.label} {chosen.isSuggested && <span className="tk-muted">added by the guide</span>}
					</span>
					<span className="tk-muted">{source.reads}</span>
					{chosen.reason && <span className="playbook-build__skill-reason">Why: {chosen.reason}</span>}
					<div className="playbook-build__source-need" role="group" aria-label={`Does the playbook need ${source.label}?`}>
						<button
							type="button"
							className={clsx("playbook-build__need-option", chosen.isRequired && "playbook-build__need-option--on")}
							onClick={() => onSetSourceNeed({ key: chosen.key, isRequired: true })}
							disabled={!isOpen || isSavingSources}
							title="A client without it is not run"
						>
							Required
						</button>
						<button
							type="button"
							className={clsx("playbook-build__need-option", !chosen.isRequired && "playbook-build__need-option--on")}
							onClick={() => onSetSourceNeed({ key: chosen.key, isRequired: false })}
							disabled={!isOpen || isSavingSources}
							title="Read when it is linked; the playbook says what it could not read otherwise"
						>
							Helpful
						</button>
					</div>
				</div>
				{isOpen && (
					<button
						type="button"
						className="playbook-build__source-remove"
						onClick={() => onRemoveSource({ key: chosen.key })}
						disabled={isSavingSources}
						aria-label={`Remove ${source.label}`}
						title={`Remove ${source.label}`}
					>
						<Icon.X size={14} />
					</button>
				)}
			</div>
		);
	};

	renderSuggestedSource = suggestion => {
		const { session, isSavingSources, onAddSource } = this.props;
		const source = this.sourceNamed({ key: suggestion.key });

		return (
			<div key={suggestion.key} className="playbook-build__source playbook-build__source--suggested">
				<AgentSourceIcon icon={source.icon} />
				<div className="playbook-build__source-text">
					<span className="playbook-build__source-name">
						{source.label} <span className="tk-muted">suggested · {suggestion.isRequired ? "required" : "helpful"}</span>
					</span>
					<span className="playbook-build__skill-reason">{suggestion.reason}</span>
				</div>
				{session.status === "open" && (
					<button
						type="button"
						className="tk-button tk-button--secondary tk-button--compact"
						onClick={() => onAddSource({ key: suggestion.key, isRequired: suggestion.isRequired })}
						disabled={isSavingSources}
					>
						<Icon.Plus size={13} />
						Add
					</button>
				)}
			</div>
		);
	};

	renderOtherSources() {
		const { session, isSavingSources, onAddSource } = this.props;
		const listedKeys = [...session.sources, ...session.suggestedSources].map(source => source.key);
		const otherSources = session.sourceCatalogue.filter(source => !listedKeys.includes(source.key));
		if (session.status !== "open" || otherSources.length === 0) return null;
		return (
			<div className="playbook-build__source-choices">
				{otherSources.map(source => (
					<button
						key={source.key}
						type="button"
						className="playbook-build__source-choice"
						onClick={() => onAddSource({ key: source.key, isRequired: false })}
						disabled={isSavingSources}
						title={source.reads}
					>
						<AgentSourceIcon icon={source.icon} size="small" />
						{source.label}
					</button>
				))}
			</div>
		);
	}

	renderDocuments() {
		const { session, isUploading, onRemoveFile } = this.props;
		const isOpen = session.status === "open";
		return (
			<div className="playbook-build__documents">
				<div className="tk-text-strong">
					Documents
					{isUploading && <span className="tk-muted"> · reading…</span>}
				</div>
				{session.referenceFiles.map(file => (
					<div key={file.id} className="playbook-build__file">
						<Icon.FileText size={13} />
						<span className="playbook-build__file-name">{file.fileName}</span>
						<span className="tk-muted">{file.addedBy}</span>
						{isOpen && (
							<button type="button" className="tk-text-action tk-text-action--muted" onClick={() => onRemoveFile({ fileId: file.id })}>
								Remove
							</button>
						)}
					</div>
				))}
				{isOpen && (
					<>
						<button type="button" className="tk-text-action" onClick={this.onChooseFiles} disabled={isUploading}>
							<Icon.Paperclip size={13} />
							Add a document
						</button>
						<span className="tk-muted">A policy, checklist or template the playbook should follow — PDF, Excel, CSV or text.</span>
					</>
				)}
				<input ref={this.fileInputRef} type="file" multiple accept=".pdf,.xlsx,.csv,.txt,.md" className="playbook-build__file-input" onChange={this.onFilesChosen} />
			</div>
		);
	}

	render() {
		const { session, isSavingSources } = this.props;
		const isDraftBehind = PlaybookBuildService.isDraftBehindOnSources({ session });

		return (
			<section className="build-card tk-card">
				<div className="build-card__head">
					<span className="build-card__title">Sources</span>
					<span className="tk-muted">{isSavingSources ? "Saving…" : "what the playbook reads"}</span>
				</div>
				{session.sources.length === 0 && session.suggestedSources.length === 0 && (
					<div className="tk-muted">Add what the playbook should read, or let the guide suggest sources as the job becomes clear.</div>
				)}
				{session.sources.map(this.renderChosenSource)}
				{session.suggestedSources.map(this.renderSuggestedSource)}
				{this.renderOtherSources()}
				{isDraftBehind && <div className="playbook-build__source-note">You changed the sources since draft {session.draft.versionNumber}. Build again to use them.</div>}
				{this.renderDocuments()}
			</section>
		);
	}
}

export default PlaybookBuildSourcesCard;
